import { type FormItemProps as BaseFormItemProps, Form } from "antd";
import { useIntl } from "react-intl";
import classNames from "~utils/classNames";
import { FormSkeleton } from "../FormSkeleton";
import { useFormItemRules } from "./useFormItemRules";
import "./FormItem.scss";

export type FormItemProps<TForm> = BaseFormItemProps<TForm> & {
  showSkeleton?: boolean;
  minLength?: number;
  maxLength?: number;
  noLeadingOrTrailingSpaces?: boolean;
  hideLabel?: boolean;
};

export const FormItem = <TForm = unknown>({ showSkeleton, label, className, hideLabel, ...props }: FormItemProps<TForm>) => {
  const { formatMessage } = useIntl();
  const fieldNameIntlKey = `form.field.label.${props.name}`;
  const resolvedLabel = hideLabel ? undefined : (label ?? formatMessage({ id: fieldNameIntlKey }));

  const validationRules = useFormItemRules({
    required: props.required,
    minLength: props.minLength,
    maxLength: props.maxLength,
    noLeadingOrTrailingSpaces: props.noLeadingOrTrailingSpaces,
    fieldLabel: resolvedLabel,
  });

  return showSkeleton ? (
    <FormSkeleton label={resolvedLabel} className={className} />
  ) : (
    <Form.Item<TForm>
      {...props}
      rules={[...(props.rules || []), ...validationRules]}
      label={resolvedLabel}
      className={classNames(className, "form-item")}
    />
  );
};
